/**
 * claimsYMap.js
 *
 * Client API for claims, suggested references, and extraction metadata
 * stored inside the unified Y.Map("referenceMarks").
 *
 * Structure consumed by this module:
 *
 *   Y.Map("referenceMarks")
 *     └── [referenceMarkId]: Y.Map
 *           ├── claims: Y.Map              ← backend writes
 *           │     └── [claimId]: Y.Map     ← individual claim data
 *           ├── suggestedReferences: Y.Map ← backend writes / client deletes
 *           │     └── [localId]: Y.Map     ← AI-suggested citations
 *           └── _meta: Y.Map               ← backend writes (status, hashes, errors)
 *
 * The backend owns suggestedReferences, claims, and _meta. The client may
 * delete individual entries from suggestedReferences when a user dismisses
 * or accepts a suggestion (removeSuggestedReference).
 *
 * Backend transaction origin: 'backend-claims-update'
 * Client UndoManager only tracks origin=null, so backend writes are never undone.
 */

import * as Y from 'yjs';
import {mergeContradictionGroups, parseMetaJson} from '../utils/contradictionGroups';
import {initializeReferenceMark} from './referencesYMap';

const CLAIMS_KEY = 'claims';
const SUGGESTED_KEY = 'suggestedReferences';
const META_KEY = '_meta';

// ---------------------------------------------------------------------------
// Deserialisation helpers
// ---------------------------------------------------------------------------

/**
 * Deserialise a single claim Y.Map into a plain JS object.
 * JSON-stringified arrays/values are parsed back.
 *
 * Claim shape (written by backend):
 * {
 *   id, claim, importance, semanticQuery, keywordsQuery,
 *   status, referenceHash, statementHash, isActive,
 *   supportingEvidence:    string[] (localIds),
 *   contradictingEvidence: string[] (localIds),
 *   extractedAt, verifiedAt
 * }
 *
 * @param {import('yjs').Map} claimMap
 * @returns {Object}
 */
function deserialiseClaim(claimMap) {
    const obj = {};
    claimMap.forEach((value, key) => {
        obj[key] = value;
    });

    // supportingEvidence and contradictingEvidence may be stored as:
    //   - JSON-stringified string (original backend format)
    //   - Y.Array (if backend writes a nested Y.Array into the claim Y.Map)
    //   - plain JS Array (already deserialized)
    const toPlainArray = (val) => {
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') {
            try {
                return JSON.parse(val);
            } catch {
                return [];
            }
        }
        // Y.Array — call toArray() to get a plain JS array
        if (val && typeof val.toArray === 'function') return val.toArray();
        return [];
    };

    obj.supportingEvidence = toPlainArray(obj.supportingEvidence);
    obj.contradictingEvidence = toPlainArray(obj.contradictingEvidence);

    // Parse supportingEvidenceTexts — discovery snippets from autonomous search,
    // stored as JSON-stringified [{referenceId, text, docTitle, docId, segmentIndex, …}].
    if (typeof obj.supportingEvidenceTexts === 'string') {
        try {
            obj.supportingEvidenceTexts = JSON.parse(obj.supportingEvidenceTexts);
        } catch {
            obj.supportingEvidenceTexts = [];
        }
    }
    if (!Array.isArray(obj.supportingEvidenceTexts)) {
        obj.supportingEvidenceTexts = [];
    }

    // Add snake_case aliases so components that pre-date the Y.Map migration still work.
    // These mirror the camelCase fields written by the backend.
    obj.supporting_evidence = obj.supportingEvidence;
    obj.contradicting_evidence = obj.contradictingEvidence;
    obj.keywords_query = obj.keywordsQuery ?? null;
    obj.semantic_query = obj.semanticQuery ?? null;

    return obj;
}

/**
 * Deserialise a single suggested-reference Y.Map into a plain JS object.
 *
 * Suggested reference shape (written by backend):
 * {
 *   localId, docId, docUrl, docTitle, docSource, docSourceType,
 *   sourceScore, content, segmentId, index, offset,
 *   start (JSON-stringified), end (JSON-stringified), tags (JSON-stringified),
 *   score, suggestedAt
 * }
 *
 * @param {import('yjs').Map} refMap
 * @returns {Object}
 */
function deserialiseSuggested(refMap) {
    const obj = {};
    refMap.forEach((value, key) => {
        obj[key] = value;
    });

    if (typeof obj.start === 'string') {
        try {
            obj.start = JSON.parse(obj.start);
        } catch {
            obj.start = null;
        }
    }
    if (typeof obj.end === 'string') {
        try {
            obj.end = JSON.parse(obj.end);
        } catch {
            obj.end = null;
        }
    }
    if (typeof obj.tags === 'string') {
        try {
            obj.tags = JSON.parse(obj.tags);
        } catch {
            obj.tags = [];
        }
    }
    if (!Array.isArray(obj.tags)) obj.tags = [];

    if (typeof obj.forClaimIds === 'string') {
        try {
            obj.forClaimIds = JSON.parse(obj.forClaimIds);
        } catch {
            obj.forClaimIds = [];
        }
    }
    if (!Array.isArray(obj.forClaimIds)) obj.forClaimIds = [];

    if (typeof obj.claimCoverage === 'string') {
        try {
            obj.claimCoverage = JSON.parse(obj.claimCoverage);
        } catch {
            obj.claimCoverage = [];
        }
    }
    if (!Array.isArray(obj.claimCoverage)) obj.claimCoverage = [];
    obj.claimCoverage = obj.claimCoverage
        .map((row) => ({
            claimId: row?.claimId,
            coverage: row?.coverage != null ? Number(row.coverage) : null,
        }))
        .filter((row) => row.claimId != null && row.coverage != null && !Number.isNaN(row.coverage));

    if (obj.proofLevel != null && obj.proofLevel !== '') {
        const n = Number(obj.proofLevel);
        obj.proofLevel = Number.isFinite(n) ? n : null;
    } else {
        obj.proofLevel = null;
    }

    return obj;
}

// ---------------------------------------------------------------------------
// Public read API
// ---------------------------------------------------------------------------

/**
 * Return the extraction/verification metadata for a given mark as a plain object.
 * Returns sensible defaults when no backend data has been written yet.
 *
 * Shape:
 * {
 *   statementHash:    string | null,
 *   status:           "idle" | "extracting" | "verifying" | "ready" | "error",
 *   error:            string | null,
 *   extractedAt:      number | null,
 *   verifiedAt:       number | null,
 *   lastProcessedAt:  number | null,
 * }
 *
 * @param {import('yjs').Map} yReferenceMarks
 * @param {string}            referenceMarkId
 * @returns {Object}
 */
export function getClaimsMeta(yReferenceMarks, referenceMarkId) {
    const defaults = {
        statementHash: null,
        status: 'idle',
        error: null,
        extractedAt: null,
        verifiedAt: null,
        lastProcessedAt: null,
    };

    if (!yReferenceMarks || !referenceMarkId) return defaults;

    const markMap = yReferenceMarks.get(referenceMarkId);
    if (!markMap) return defaults;
    const metaMap = markMap.get(META_KEY);
    if (!metaMap) return defaults;

    const obj = {...defaults};
    metaMap.forEach((value, key) => {
        obj[key] = value;
    });

    obj.contradictions = mergeContradictionGroups(
        parseMetaJson(obj.contradictions, [])
    );
    obj.extrinsicNotes = parseMetaJson(obj.extrinsicNotes, {});

    return obj;
}

/**
 * Return all active claims and the extraction metadata for a given reference mark.
 *
 * Returns a combined object so callers get both pieces in a single call —
 * matching the shape consumed by useYClaimAnalysis:
 *   { claims: Object[], meta: Object }
 *
 * Claims are sorted by id ascending (the order the LLM extracted them).
 * Only claims with isActive !== false are included.
 *
 * @param {import('yjs').Map} yReferenceMarks  Root Y.Map("referenceMarks")
 * @param {string}            referenceMarkId
 * @returns {{ claims: Object[], meta: Object }}
 */
export function getClaimsForMark(yReferenceMarks, referenceMarkId) {
    const meta = getClaimsMeta(yReferenceMarks, referenceMarkId);

    if (!yReferenceMarks || !referenceMarkId) return {claims: [], meta};

    const markMap = yReferenceMarks.get(referenceMarkId);
    if (!markMap) return {claims: [], meta};

    const claimsMap = markMap.get(CLAIMS_KEY);
    const claims = [];

    if (claimsMap) {
        claimsMap.forEach((claimMap) => {
            if (claimMap && claimMap.get('isActive') !== false) {
                claims.push(deserialiseClaim(claimMap));
            }
        });
        claims.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    }

    return {claims, meta};
}

/**
 * Compute a claims summary from the active claims for a mark.
 * Pure read — does not write anything to the Y.Map.
 *
 * @param {import('yjs').Map} yReferenceMarks
 * @param {string}            referenceMarkId
 * @returns {{
 *   totalClaims:            number,
 *   provenClaims:           number,
 *   partiallyProvenClaims:  number,
 *   contradictedClaims:     number,
 *   unprovenClaims:         number,
 * }}
 */
export function computeClaimsSummary(yReferenceMarks, referenceMarkId) {
    const summary = {
        totalClaims: 0,
        provenClaims: 0,
        partiallyProvenClaims: 0,
        contradictedClaims: 0,
        unprovenClaims: 0,
    };

    const {claims} = getClaimsForMark(yReferenceMarks, referenceMarkId);
    summary.totalClaims = claims.length;

    claims.forEach((claim) => {
        switch (claim.status) {
            case 'proven':
                summary.provenClaims++;
                break;
            case 'partially_proven':
                summary.partiallyProvenClaims++;
                break;
            case 'contradicted':
                summary.contradictedClaims++;
                break;
            default:
                summary.unprovenClaims++;
        }
    });

    return summary;
}

/**
 * Return all suggested references for a given mark, sorted by score descending.
 * Returns an empty array when the backend has not yet populated suggestions.
 *
 * @param {import('yjs').Map} yReferenceMarks
 * @param {string}            referenceMarkId
 * @returns {Object[]}
 */
export function getSuggestedReferences(yReferenceMarks, referenceMarkId) {
    if (!yReferenceMarks || !referenceMarkId) return [];

    const markMap = yReferenceMarks.get(referenceMarkId);
    if (!markMap) return [];
    const suggestedMap = markMap.get(SUGGESTED_KEY);
    if (!suggestedMap) return [];

    const result = [];
    suggestedMap.forEach((refMap) => {
        if (refMap) result.push(deserialiseSuggested(refMap));
    });

    // Higher score = more relevant → sort descending
    result.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    return result;
}

// ---------------------------------------------------------------------------
// Write API (client-side dismissal of suggestions)
// ---------------------------------------------------------------------------

/**
 * Delete a single suggested reference from the Y.Map.
 * Called when the user accepts (adds) or dismisses a suggestion so the entry
 * is removed from every peer's view and the suggestion counter drops immediately.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  yReferenceMarks
 * @param {string}             referenceMarkId
 * @param {string}             localId           Key used inside suggestedReferences Y.Map
 */
export function removeSuggestedReference(ydoc, yReferenceMarks, referenceMarkId, localId) {
    if (!ydoc || !yReferenceMarks || !referenceMarkId || !localId) return;

    const markMap = yReferenceMarks.get(referenceMarkId);
    if (!markMap) return;

    const suggestedMap = markMap.get(SUGGESTED_KEY);
    if (!suggestedMap) return;

    ydoc.transact(() => {
        suggestedMap.delete(localId);
    }, null);
}

// ---------------------------------------------------------------------------
// Observation
// ---------------------------------------------------------------------------

/**
 * Subscribe to ALL changes within a single reference mark's subtree.
 * One observer covers addedReferences, claims, suggestedReferences, and _meta —
 * replacing three separate Firestore subscriptions.
 *
 * @param {import('yjs').Map} yReferenceMarks
 * @param {string}            referenceMarkId
 * @param {Function}          callback  (events: Y.YEvent[]) => void
 * @returns {Function} Unsubscribe function
 */
export function observeMark(yReferenceMarks, referenceMarkId, callback) {
    if (!yReferenceMarks || !referenceMarkId || typeof callback !== 'function') return () => {
    };

    const handler = (events) => {
        const relevant = events.some((event) =>
            (event.path.length === 0 && event.keysChanged && event.keysChanged.has(referenceMarkId))
            || (event.path.length > 0 && event.path[0] === referenceMarkId)
        );
        if (relevant) callback(events);
    };

    yReferenceMarks.observeDeep(handler);
    return () => yReferenceMarks.unobserveDeep(handler);
}

/**
 * Subscribe to any change anywhere inside Y.Map("referenceMarks").
 * Useful when a component needs to react to changes across all marks.
 *
 * @param {import('yjs').Map} yReferenceMarks
 * @param {Function}          callback  (events: Y.YEvent[]) => void
 * @returns {Function} Unsubscribe function
 */
export function observeAllMarks(yReferenceMarks, callback) {
    if (!yReferenceMarks || typeof callback !== 'function') return () => {
    };
    yReferenceMarks.observeDeep(callback);
    return () => yReferenceMarks.unobserveDeep(callback);
}

/**
 * Ask the backend to extract claims for a reference mark that has not been
 * processed yet. Ensures the Y.Map entry exists, then sets `_meta.forceRefresh`
 * which ws-collaboration observes and turns into a full extraction run.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yReferenceMarks
 * @param {string} referenceMarkId
 */
export function requestClaimsExtraction(ydoc, yReferenceMarks, referenceMarkId) {
    if (!ydoc || !yReferenceMarks || !referenceMarkId) return;

    ydoc.transact(() => {
        initializeReferenceMark(ydoc, yReferenceMarks, referenceMarkId);
        const markMap = yReferenceMarks.get(referenceMarkId);
        if (!markMap) return;

        let metaMap = markMap.get(META_KEY);
        if (!metaMap) {
            metaMap = new Y.Map();
            markMap.set(META_KEY, metaMap);
        }
        metaMap.set('forceRefresh', true);
    }, null);
}

